import { createRouter, createWebHistory } from 'vue-router'
import { useAppStore } from '../stores/app'
import { t } from '../lib/i18n'

/**
 * Stiene er engelske, rutenavnene norske som resten av koden.
 *
 * Adressen er det eneste av appen som er synlig utenfra - den deles, limes inn
 * og står i statistikken - og der er engelsk den formen flest kan lese. Inne i
 * koden navigeres det alltid med `{ name: '…' }`, så navnene er interne og
 * følger språket kodebasen ellers er skrevet på.
 *
 * `alias` holder de gamle norske stiene i live: en bokmerket /minside skal
 * fortsatt åpne barnets side, ikke havne på velkomstsiden. Et treff på et
 * alias beholder adressen slik den ble skrevet; appen selv lenker alltid til
 * den engelske, siden all navigasjon går på navn. Aliasene kan fjernes den
 * dagen ingen har gamle bokmerker igjen.
 *
 * `title` er nøkkelen til fanetittelen, og er en tekst som allerede finnes i
 * grensesnittet - ingen egne tekster å oversette for tittelens skyld.
 */
const routes = [
  { path: '/', name: 'root', redirect: () => ({ name: 'oversikt' }) },

  // Innlogging
  { path: '/welcome', alias: '/velkommen', name: 'velkommen', component: () => import('../views/WelcomeView.vue'), meta: { public: true } },
  { path: '/sign-in', alias: '/logg-inn', name: 'logg-inn', component: () => import('../views/SignInView.vue'), meta: { public: true, title: 'Logg inn' } },
  { path: '/sign-up', alias: '/registrer', name: 'registrer', component: () => import('../views/SignUpView.vue'), meta: { public: true, title: 'Opprett konto' } },
  { path: '/child-sign-in', alias: '/barn', name: 'barn-innlogging', component: () => import('../views/ChildSignInView.vue'), meta: { public: true, title: 'Logg inn' } },
  { path: '/reset-password', alias: '/nytt-passord', name: 'nytt-passord', component: () => import('../views/ResetPasswordView.vue'), meta: { public: true, title: 'Nytt passord' } },

  // Første gangs oppsett
  { path: '/setup', alias: '/oppsett', name: 'oppsett', component: () => import('../views/SetupView.vue'), meta: { setup: true } },

  // Forelder
  { path: '/overview', alias: '/oversikt', name: 'oversikt', component: () => import('../views/ParentHomeView.vue'), meta: { role: 'parent', title: 'Oversikt' } },
  { path: '/chores', alias: '/oppgaver', name: 'oppgaver', component: () => import('../views/TasksAdminView.vue'), meta: { role: 'parent', title: 'Oppgaver' } },
  { path: '/children/:id', alias: '/barn/:id', name: 'barn-detalj', component: () => import('../views/ChildDetailView.vue'), meta: { role: 'parent', title: 'Barn' } },
  { path: '/challenges', alias: '/utfordringer', name: 'utfordringer', component: () => import('../views/ChallengesView.vue'), meta: { role: 'parent', title: 'Utfordringer' } },
  { path: '/family', alias: '/familie', name: 'familie', component: () => import('../views/FamilyView.vue'), meta: { role: 'parent', title: 'Familie' } },

  // Barn
  { path: '/my-account', alias: '/minside', name: 'minside', component: () => import('../views/ChildHomeView.vue'), meta: { role: 'child', title: 'Min side' } },
  { path: '/my-chores', alias: '/gjore', name: 'gjore', component: () => import('../views/ChildTasksView.vue'), meta: { role: 'child', title: 'Gjøremål' } },
  { path: '/history', alias: '/historikk', name: 'historikk', component: () => import('../views/ChildHistoryView.vue'), meta: { role: 'child', title: 'Historikk' } },
  { path: '/my-profile', alias: '/minprofil', name: 'minprofil', component: () => import('../views/ChildProfileView.vue'), meta: { role: 'child', title: 'Min profil' } },
  { path: '/my-challenges', alias: '/mine-utfordringer', name: 'mine-utfordringer', component: () => import('../views/ChildChallengesView.vue'), meta: { role: 'child', title: 'Utfordringer' } },

  { path: '/:pathMatch(.*)*', redirect: '/' },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
})

/**
 * Tittelen fra index.html. Den er skrevet for søkeresultatet og er den eneste
 * av appens tekster som leses utenfra, så velkomstsiden beholder den - resten
 * av sidene er innlogget og har bruk for en kortere tittel i faneraden.
 */
const SITE_TITLE = document.title

router.afterEach((to) => {
  document.title = to.meta.title ? `${t(to.meta.title)} – Bonto` : SITE_TITLE
})

router.beforeEach(async (to) => {
  const store = useAppStore()

  // Vent til vi vet om brukeren allerede er innlogget, ellers blir første
  // navigasjon sendt til innlogging selv om det finnes en gyldig session.
  if (store.booting) await store.init()

  if (!store.isLoggedIn) {
    return to.meta.public ? true : { name: 'velkommen' }
  }

  if (store.needsSetup) {
    return to.meta.setup ? true : { name: 'oppsett' }
  }

  const home = store.isParent ? { name: 'oversikt' } : { name: 'minside' }

  if (to.meta.public || to.meta.setup) return home
  if (to.meta.role && to.meta.role !== store.profile.role) return home

  return true
})
